import axiosInstance from "./axiosInstance";

import { API_V2 } from "@Models/GWDomain";

export const giftcardsBaseUrl = `${API_V2}/giftcards`;
export const promotionsBaseUrl = `${API_V2}/promotions`;
export const loungesBaseUrl = `${API_V2}/locals`;
export const accountBaseUrl = `${API_V2}/account`;
export const paymenthsBaseUrl = `${API_V2}/payments`;
export const cashRegisterBaseUrl = `${API_V2}/pos`;
export const servicesBaseUrl = `${API_V2}/services`;
export const productsBaseUrl = `${API_V2}/products`;
export const billingsBaseUrl = `${API_V2}/billing`;
export const clientsBaseUrl = `${API_V2}/customers`;

/**
 * @deprecated No usar directamente. El token ahora se obtiene dinamicamente via interceptor.
 * Mantenido solo por compatibilidad con codigo legacy que pueda importarlo.
 */
export const tokenLocal = localStorage.getItem("access_token");

/**
 * @deprecated No usar directamente. El header de autorizacion ahora se agrega
 * automaticamente via interceptor en cada request.
 * Mantenido solo por compatibilidad con codigo legacy que pueda importarlo.
 */
export const headerAuthorization = {
  headers: {
    Authorization: `Bearer ${tokenLocal}`,
  },
};
export interface ApiResponse {
  isSuccess: boolean;
  status: number;
  data: any;
  message: string;
  /** Código tipificado del backend en errores (ej. NO_ACTIVE_SUBSCRIPTION). */
  code?: string;
}

export const handleSuccessfullResponse = (
  response: any,
  successMessage: string
): ApiResponse => ({
  isSuccess: true,
  status: response?.status || 200,
  data: response.data.data,
  message: response?.data?.message || successMessage,
});

/**
 * Mensajes que NestJS/class-validator emite para el desarrollador, no para el
 * usuario. Se descartan: el dueño del producto llegó a ver, dentro del modal
 * de compra, un literal `type must be one of the following values: user,
 * comanda_user, cashbox, warehouse`.
 */
const TECHNICAL_MESSAGE_PATTERNS: RegExp[] = [
  /\bmust (be|not|contain|match|have)\b/i,
  /\bshould not\b/i,
  /\beach value in\b/i,
  /\bproperty .+ (has|should)\b/i,
  /^(internal server error|bad request|unauthorized|forbidden|not found|unprocessable entity)$/i,
  /cannot read propert/i,
  /is not a function/i,
  /\bundefined\b/i,
  /^ER_[A-Z_]+/,
  /\bSQLSTATE\b/i,
  /\b\w*Error:\s/,
];

const isUsableMessage = (value: unknown): value is string =>
  typeof value === "string" &&
  value.trim().length > 0 &&
  !TECHNICAL_MESSAGE_PATTERNS.some((re) => re.test(value.trim()));

/**
 * Normaliza el `message` del backend a UN texto mostrable.
 *
 * Cuando NestJS rechaza por validación, `message` es un ARRAY de strings y el
 * panel lo pintaba tal cual (array crudo, en inglés técnico). Criterio:
 * - array → se conservan solo los textos escritos para el usuario; si quedan
 *   dos cortos se unen con " · " (dos errores de formulario caben en un modal
 *   sin volverse un muro de texto), si hay más se muestra el primero, que es
 *   el del primer campo en fallar;
 * - string técnico o vacío → se cae al `errorMessage` del servicio, que ya
 *   está redactado en español y en contexto de la acción.
 */
export const normalizeApiErrorMessage = (
  raw: unknown,
  fallback: string
): string => {
  const candidates = (Array.isArray(raw) ? raw : [raw])
    .filter(isUsableMessage)
    .map((m) => m.trim());
  if (candidates.length === 0) return fallback;
  const joined = candidates.slice(0, 2).join(" · ");
  if (candidates.length <= 2 && joined.length <= 160) return joined;
  return candidates[0];
};

export const handleApiError = (
  error: any,
  errorMessage: string
): ApiResponse => {
  return {
    isSuccess: false,
    status: error?.response?.status || 500,
    data: null,
    message: normalizeApiErrorMessage(
      error?.response?.data?.message,
      errorMessage
    ),
    // Código tipificado del backend (ej. NO_ACTIVE_SUBSCRIPTION): permite
    // reaccionar con una salida concreta en vez de mostrar el texto crudo.
    code: error?.response?.data?.code,
  };
};

/**
 * Realiza una peticion GET usando la instancia de axios configurada.
 * El token de autorizacion se obtiene dinamicamente del localStorage en cada request.
 */
export const getData = async (
  url: string,
  successMessage: string,
  errorMessage: string
) => {
  try {
    const response = await axiosInstance.get(url);
    return handleSuccessfullResponse(response, successMessage);
  } catch (error) {
    return handleApiError(error, errorMessage);
  }
};

/**
 * Realiza una peticion POST usando la instancia de axios configurada.
 * El token de autorizacion se obtiene dinamicamente del localStorage en cada request.
 */
export const postData = async (
  url: string,
  data: any,
  successMessage: string,
  errorMessage: string
) => {
  try {
    const response = await axiosInstance.post(url, data);
    return handleSuccessfullResponse(response, successMessage);
  } catch (error) {
    return handleApiError(error, errorMessage);
  }
};

/**
 * Realiza una peticion PUT usando la instancia de axios configurada.
 * El token de autorizacion se obtiene dinamicamente del localStorage en cada request.
 */
export const putData = async (
  url: string,
  data: any,
  successMessage: string,
  errorMessage: string
) => {
  try {
    const response = await axiosInstance.put(url, data);
    return handleSuccessfullResponse(response, successMessage);
  } catch (error) {
    return handleApiError(error, errorMessage);
  }
};

/**
 * Realiza una peticion PATCH usando la instancia de axios configurada.
 * El token de autorizacion se obtiene dinamicamente del localStorage en cada request.
 */
export const patchData = async (
  url: string,
  data: any,
  successMessage: string,
  errorMessage: string
) => {
  try {
    const response = await axiosInstance.patch(url, data);
    return handleSuccessfullResponse(response, successMessage);
  } catch (error) {
    return handleApiError(error, errorMessage);
  }
};

/**
 * Realiza una peticion DELETE usando la instancia de axios configurada.
 * El token de autorizacion se obtiene dinamicamente del localStorage en cada request.
 */
export const deleteData = async (
  url: string,
  successMessage: string,
  errorMessage: string
) => {
  try {
    const response = await axiosInstance.delete(url);
    return handleSuccessfullResponse(response, successMessage);
  } catch (error) {
    return handleApiError(error, errorMessage);
  }
};
